import React, { useEffect, useState } from "react";
import { backend_url } from "../App";
import ReactPaginate from "react-paginate";
import "./CSS/Orders.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DeliveryBill from "../Components/DeliveryBill/DeliveryBill";
import { exportCsv } from "../utils/exportCsv";
import { getAdminId } from "../utils/roleUtils";

const OrderDetails = () => {
  const [orders, setOrders] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [activeTab, setActiveTab] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [editingOrder, setEditingOrder] = useState(null);
  const [viewOrder, setViewOrder] = useState(null);
  const ordersPerPage = 5;
  const [dateFilter, setDateFilter] = useState("");
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalCost, setTotalCost] = useState(0);
  const [pendingOrders, setPendingOrders] = useState(0);
  const [completedOrders, setCompletedOrders] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [billOrder, setBillOrder] = useState(null);
  const [previewImg, setPreviewImg] = useState(null);
  const [refundOrder, setRefundOrder] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [refunding, setRefunding] = useState(false);

  useEffect(() => {
    fetchOrderDetails();
  }, []); //
  useEffect(() => {
    // Move the stats calculation to a separate effect
    const filtered = orders.filter((order) =>
      dateFilter
        ? new Date(order.orderDate).toLocaleDateString("en-CA") === dateFilter
        : true
    );

    const total = filtered.length;
    const cost = filtered.reduce((sum, order) => sum + order.totalPrice, 0);
    const pending = filtered.filter(
      (order) =>
        order.status === "Pending" ||
        order.status === "Packed" ||
        order.status === "Shipped"
    ).length;
    const completed = filtered.filter(
      (order) => order.status === "Delivered"
    ).length;

    setTotalOrders(total);
    setTotalCost(cost);
    setPendingOrders(pending);
    setCompletedOrders(completed);
  }, [orders, dateFilter]);

  const fetchOrderDetails = async () => {
    try {
      const response = await fetch(`${backend_url}/allorders`, {
        method: "GET",
      });

      const data = await response.json();

      if (data.success) {
        setOrders(data.orders);

        const total = data.orders.length;
        const cost = data.orders.reduce(
          (sum, order) => sum + order.totalPrice,
          0
        );
        const pending = data.orders.filter(
          (order) => order.status === "Pending"
        ).length;
        const completed = data.orders.filter(
          (order) => order.status === "Completed"
        ).length;

        setTotalOrders(total);
        setTotalCost(cost);
        setPendingOrders(pending);
        setCompletedOrders(completed);
      } else {
        console.error("Failed to fetch order details:", data.message);
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
    }
  };

  // Fetch order details by ID
  const fetchOrderById = async (id) => {
    try {
      const response = await fetch(`${backend_url}/order/${id}`);
      const data = await response.json();

      if (data.success) {
        setViewOrder(data.order);
      } else {
        console.error("Failed to fetch order details:", data.message);
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
    }
  };

  // Filter orders based on tab
  const filteredOrders = orders.filter((order) => {
    if (activeTab === "All") return true;
    if (activeTab === "Progress")
      return ["Pending", "Confirmed", "Packed", "Shipped"].includes(
        order.status
      );
    if (activeTab === "Completed") return ["Delivered"].includes(order.status);
    if (activeTab === "Cancelled") return ["Cancelled"].includes(order.status);
    if (activeTab === "Refunded")
      return ["Return Requested", "Refunded"].includes(order.status);
    return true;
  });

  // Search within the filtered list
  // const searchedOrders = filteredOrders.filter(
  //   (order) =>
  //     order.productId?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
  //     order.userId?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  // );

  // Search + Date Filter
  const searchedOrders = filteredOrders.filter((order) => {
    const matchSearch =
      order.productId?.name
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase()) ||
      order.userId?.name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchDate = dateFilter
      ? new Date(order.orderDate).toLocaleDateString("en-CA") === dateFilter
      : true;

    return matchSearch && matchDate;
  });

  // Pagination
  const offset = currentPage * ordersPerPage;
  const currentOrders = searchedOrders.slice(offset, offset + ordersPerPage);
  const pageCount = Math.ceil(searchedOrders.length / ordersPerPage);

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  const handleExportOrders = () => {
    const header = ["Order ID", "Product", "Color", "Customer", "Status", "Order Date", "Total Price", "Refund Status", "Refund Amount"];
    const rows = searchedOrders.map((order) => [
      order._id,
      order.productId?.name || "",
      order.color || "",
      order.userId?.name || "",
      order.status,
      order.orderDate ? new Date(order.orderDate).toLocaleDateString("en-IN") : "",
      order.totalPrice,
      order.refundStatus || "None",
      order.refundAmount || 0,
    ]);
    exportCsv("orders.csv", header, rows);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(0);
  };

  const handleViewDelivery = (order) => {
    setSelectedOrder(order);
  };

  const handleCloseModal = () => {
    setSelectedOrder(null);
  };

  // Handle edit
  const handleEdit = (order) => {
    setEditingOrder({ ...order, _originalStatus: order.status });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch(
        `${backend_url}/edit-order/${editingOrder._id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            size: "None",
            quantity: editingOrder.quantity,
            status: editingOrder.status,
            adminId: getAdminId(),
            adminName: sessionStorage.getItem("adminName"),
            adminRole: sessionStorage.getItem("adminRole"),
          }),
        }
      );

      const data = await response.json();
      if (!data.success) {
        toast.error("Failed to update order");
        return;
      }

      // Save tracking info if provided
      if (editingOrder.courierName || editingOrder.trackingId) {
        await fetch(`${backend_url}/updatetracking/${editingOrder._id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            courierName: editingOrder.courierName || "",
            trackingId: editingOrder.trackingId || "",
          }),
        });
      }

      toast.success("Order updated successfully");
      fetchOrderDetails();
      setEditingOrder(null);
    } catch (error) {
      toast.error("Error updating order");
    }
  };
  const statusClass = (s) =>
    s === "Pending" ? "pending"
    : s === "Confirmed" ? "confirmed"
    : s === "Packed" ? "packed"
    : s === "Shipped" ? "shipped"
    : s === "Delivered" ? "delivered"
    : s === "Cancelled" ? "cancelled"
    : s === "Return Requested" ? "return-requested"
    : s === "Refunded" ? "refunded"
    : "";

  const openRefund = (order) => {
    setRefundOrder(order);
    setRefundAmount(order.totalPrice);
    setRefundReason("");
  };

  const closeRefund = () => {
    setRefundOrder(null);
    setRefundAmount("");
    setRefundReason("");
  };

  const submitRefund = async (e) => {
    e.preventDefault();
    setRefunding(true);
    try {
      const res = await fetch(`${backend_url}/refund-order/${refundOrder._id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: refundAmount,
          reason: refundReason,
          adminId: getAdminId(),
          adminName: sessionStorage.getItem("adminName"),
          adminRole: sessionStorage.getItem("adminRole"),
        }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success("Refund processed successfully");
        fetchOrderDetails();
        closeRefund();
      } else {
        toast.error(data.message || "Refund failed");
      }
    } catch (error) {
      toast.error("Error processing refund");
    } finally {
      setRefunding(false);
    }
  };

  return (
    <>
      <div className="orders-page">

        {/* ── Header ── */}
        <div className="orders-header">
          <div className="orders-title-row">
            <h2 className="orders-title">
              <span className="orders-title-icon">🛒</span>
              Order Details
            </h2>
            <span className="orders-total-badge">{totalOrders} orders</span>
            <button
              className="orders-export-btn"
              onClick={handleExportOrders}
              disabled={!searchedOrders.length}
            >
              ⬇ Export CSV
            </button>
          </div>

          <div className="orders-controls-row">
            <div className="orders-tabs">
              {["All", "Progress", "Completed", "Cancelled", "Refunded"].map((tab) => (
                <button
                  key={tab}
                  className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            <div className="orders-search-group">
              <div className="search-input-wrap">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="date-picker"
              />
            </div>
          </div>
        </div>

        {/* ── Stat Cards ── */}
        <div className="stats-row">
          <div className="stat-card card-total">
            <div className="stat-icon-wrap">📦</div>
            <div className="stat-info">
              <span className="stat-label">Total Orders</span>
              <span className="stat-value">{totalOrders}</span>
            </div>
          </div>
          <div className="stat-card card-revenue">
            <div className="stat-icon-wrap">💰</div>
            <div className="stat-info">
              <span className="stat-label">Total Revenue</span>
              <span className="stat-value">₹{totalCost.toFixed(0)}</span>
            </div>
          </div>
          <div className="stat-card card-pending">
            <div className="stat-icon-wrap">⏳</div>
            <div className="stat-info">
              <span className="stat-label">Pending Orders</span>
              <span className="stat-value">{pendingOrders}</span>
            </div>
          </div>
          <div className="stat-card card-done">
            <div className="stat-icon-wrap">✅</div>
            <div className="stat-info">
              <span className="stat-label">Completed</span>
              <span className="stat-value">{completedOrders}</span>
            </div>
          </div>
          <div className="stat-card card-refunded">
            <div className="stat-icon-wrap">↩</div>
            <div className="stat-info">
              <span className="stat-label">Refunded</span>
              <span className="stat-value">
                {orders.filter((o) => o.refundStatus === "Refunded").length}
              </span>
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="orders-table-wrapper">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>Color</th>
                <th>Customer</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Status</th>
                <th>Order Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentOrders.length > 0 ? (
                currentOrders.map((order, idx) => (
                  <tr key={order._id}>
                    <td>
                      <div className="order-serial">{offset + idx + 1}</div>
                    </td>
                    <td>
                      <div className="product-cell">
                        <img
                          src={order.productId?.images?.[0] || order.images?.[0]}
                          alt={order.productId?.name}
                          className="product-thumb product-thumb-clickable"
                          onClick={() => setPreviewImg({
                            src: order.productId?.images?.[0] || order.images?.[0],
                            name: order.productId?.name,
                            id: order.productId?.unique_id,
                          })}
                          title="Click to enlarge"
                        />
                        <div className="product-info">
                          <span className="product-id">{order.productId?.unique_id || "N/A"}</span>
                          <span className="product-name">{order.productId?.name || "N/A"}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      {order.color ? (
                        <span className="order-color-cell">
                          <span
                            className="order-color-dot"
                            style={{ background: order.colorHex || "#ccc" }}
                          />
                          {order.color}
                        </span>
                      ) : "—"}
                    </td>
                    <td>{order.name || "—"}</td>
                    <td>{order.quantity}</td>
                    <td className="order-price">₹{order.totalPrice.toFixed(2)}</td>
                    <td>
                      <span className={`status-badge ${statusClass(order.status)}`}>
                        {order.status}
                      </span>
                    </td>
                    <td>{new Date(order.orderDate).toLocaleDateString()}</td>
                    <td>
                      <div className="action-group">
                        <button onClick={() => handleEdit(order)} className="btn-edit">
                          Edit
                        </button>
                        <button onClick={() => handleViewDelivery(order)} className="btn-delivery">
                          Delivery
                        </button>
                        {["Confirmed", "Packed", "Shipped", "Delivered"].includes(order.status) && (
                          <button onClick={() => setBillOrder(order)} className="btn-bill">
                            🧾 Bill
                          </button>
                        )}
                        {order.paymentStatus === "Paid" && order.refundStatus !== "Refunded" && (
                          <button onClick={() => openRefund(order)} className="btn-refund">
                            ↩ Refund
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8">
                    <div className="empty-orders">
                      <div className="empty-orders-icon">📭</div>
                      No orders found
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {searchedOrders.length > ordersPerPage && (
          <div className="pagination-wrapper">
            <ReactPaginate
              previousLabel="← Prev"
              nextLabel="Next →"
              breakLabel="..."
              pageCount={pageCount}
              onPageChange={handlePageChange}
              containerClassName="pagination"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              breakClassName="page-item"
              breakLinkClassName="page-link"
              activeClassName="active"
              disabledClassName="disabled"
            />
          </div>
        )}

        {/* ── Delivery Details Modal ── */}
        {selectedOrder && (
          <div className="modal-overlay" onClick={handleCloseModal}>
            <div className="delivery-modal" onClick={(e) => e.stopPropagation()}>
              <div className="delivery-modal-header">
                <h3>📍 Delivery Details</h3>
                <button className="modal-header-close" onClick={handleCloseModal}>×</button>
              </div>
              <div className="delivery-modal-body">
                <div className="delivery-row">
                  <span className="delivery-row-icon">👤</span>
                  <div className="delivery-row-content">
                    <span className="delivery-row-label">Customer Name</span>
                    <span className="delivery-row-value">{selectedOrder.name || "—"}</span>
                  </div>
                </div>
                <div className="delivery-row">
                  <span className="delivery-row-icon">✉️</span>
                  <div className="delivery-row-content">
                    <span className="delivery-row-label">Email</span>
                    <span className="delivery-row-value">{selectedOrder.email || "—"}</span>
                  </div>
                </div>
                <div className="delivery-row">
                  <span className="delivery-row-icon">📞</span>
                  <div className="delivery-row-content">
                    <span className="delivery-row-label">Phone</span>
                    <span className="delivery-row-value">{selectedOrder.phoneNumber || "—"}</span>
                  </div>
                </div>
                <div className="delivery-row">
                  <span className="delivery-row-icon">🏠</span>
                  <div className="delivery-row-content">
                    <span className="delivery-row-label">Address</span>
                    <span className="delivery-row-value">
                      {[selectedOrder.address, selectedOrder.city, selectedOrder.state, selectedOrder.country]
                        .filter(Boolean).join(", ")}
                      {selectedOrder.pincode ? ` - ${selectedOrder.pincode}` : ""}
                    </span>
                  </div>
                </div>
                {selectedOrder.color && (
                  <div className="delivery-row">
                    <span className="delivery-row-icon">🎨</span>
                    <div className="delivery-row-content">
                      <span className="delivery-row-label">Color</span>
                      <span className="delivery-row-value order-color-cell">
                        <span
                          className="order-color-dot"
                          style={{ background: selectedOrder.colorHex || "#ccc" }}
                        />
                        {selectedOrder.color}
                      </span>
                    </div>
                  </div>
                )}
                {selectedOrder.courierName && (
                  <div className="delivery-row">
                    <span className="delivery-row-icon">🚚</span>
                    <div className="delivery-row-content">
                      <span className="delivery-row-label">Courier</span>
                      <span className="delivery-row-value">{selectedOrder.courierName}</span>
                    </div>
                  </div>
                )}
                {selectedOrder.trackingId && (
                  <div className="delivery-row">
                    <span className="delivery-row-icon">📦</span>
                    <div className="delivery-row-content">
                      <span className="delivery-row-label">Tracking ID</span>
                      <span className="delivery-row-value" style={{ fontFamily: 'monospace', letterSpacing: '0.5px' }}>
                        {selectedOrder.trackingId}
                      </span>
                    </div>
                  </div>
                )}
                {selectedOrder.refundStatus === "Refunded" && (
                  <div className="delivery-row">
                    <span className="delivery-row-icon">↩</span>
                    <div className="delivery-row-content">
                      <span className="delivery-row-label">Refund</span>
                      <span className="delivery-row-value">
                        ₹{selectedOrder.refundAmount} refunded on{" "}
                        {selectedOrder.refundedAt ? new Date(selectedOrder.refundedAt).toLocaleDateString() : "—"}
                        {selectedOrder.refundReason ? ` — ${selectedOrder.refundReason}` : ""}
                      </span>
                    </div>
                  </div>
                )}
                {selectedOrder.customerGSTIN && (
                  <div className="delivery-row">
                    <span className="delivery-row-icon">🏢</span>
                    <div className="delivery-row-content">
                      <span className="delivery-row-label">Customer GSTIN</span>
                      <span className="delivery-row-value" style={{ fontFamily: 'monospace' }}>
                        {selectedOrder.customerGSTIN}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Edit Modal ── */}
        {editingOrder && (
          <div className="edit-modal-overlay" onClick={() => setEditingOrder(null)}>
            <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
              <div className="edit-modal-header">
                <h3>Edit Order Status</h3>
                <button className="modal-header-close" onClick={() => setEditingOrder(null)}>×</button>
              </div>
              <form onSubmit={handleEditSubmit}>
                <div className="edit-modal-body">
                  <div className="form-group">
                    <label htmlFor="quantity">Quantity</label>
                    <input
                      id="quantity"
                      type="number"
                      disabled
                      value={editingOrder.quantity}
                      onChange={(e) => setEditingOrder({ ...editingOrder, quantity: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="status">Order Status</label>
                    <select
                      id="status"
                      value={editingOrder.status}
                      onChange={(e) => setEditingOrder({ ...editingOrder, status: e.target.value })}
                    >
                      <option value="Pending">Pending</option>
                      <option value="Confirmed">Confirmed</option>
                      <option value="Packed">Packed</option>
                      <option value="Shipped">Shipped</option>
                      <option value="Delivered">Delivered</option>
                      {editingOrder._originalStatus !== "Delivered" && (
                        <>
                          <option value="Cancelled">Cancelled</option>
                          <option value="Return Requested">Return Requested</option>
                        </>
                      )}
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="courierName">Courier Name</label>
                    <select
                      id="courierName"
                      value={editingOrder.courierName || ""}
                      onChange={(e) => setEditingOrder({ ...editingOrder, courierName: e.target.value })}
                    >
                      <option value="">Select Courier</option>
                      <option value="Delhivery">Delhivery</option>
                      <option value="BlueDart">BlueDart</option>
                      <option value="DTDC">DTDC</option>
                      <option value="Ekart">Ekart</option>
                      <option value="India Post">India Post</option>
                      <option value="Xpressbees">Xpressbees</option>
                      <option value="Shadowfax">Shadowfax</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="trackingId">Tracking ID</label>
                    <input
                      id="trackingId"
                      type="text"
                      placeholder="Enter courier tracking ID"
                      value={editingOrder.trackingId || ""}
                      onChange={(e) => setEditingOrder({ ...editingOrder, trackingId: e.target.value })}
                    />
                  </div>
                </div>
                <div className="edit-modal-footer">
                  <button type="button" className="btn-cancel-modal" onClick={() => setEditingOrder(null)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-save">
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ── Refund Modal ── */}
        {refundOrder && (
          <div className="edit-modal-overlay" onClick={closeRefund}>
            <div className="edit-modal" onClick={(e) => e.stopPropagation()}>
              <div className="edit-modal-header">
                <h3>↩ Process Refund</h3>
                <button className="modal-header-close" onClick={closeRefund}>×</button>
              </div>
              <form onSubmit={submitRefund}>
                <div className="edit-modal-body">
                  <p className="refund-order-summary">
                    Order for <strong>{refundOrder.name}</strong> — Total paid: ₹{refundOrder.totalPrice}
                  </p>
                  <div className="form-group">
                    <label htmlFor="refundAmount">Refund Amount (₹)</label>
                    <input
                      id="refundAmount"
                      type="number"
                      min="1"
                      max={refundOrder.totalPrice}
                      step="0.01"
                      value={refundAmount}
                      onChange={(e) => setRefundAmount(e.target.value)}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="refundReason">Reason</label>
                    <input
                      id="refundReason"
                      type="text"
                      placeholder="e.g. Product damaged, wrong item, customer request"
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                    />
                  </div>
                  <p className="refund-warning">
                    ⚠ This will trigger a real refund via Razorpay to the customer's original payment method.
                  </p>
                </div>
                <div className="edit-modal-footer">
                  <button type="button" className="btn-cancel-modal" onClick={closeRefund}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-save btn-refund-confirm" disabled={refunding}>
                    {refunding ? "Processing..." : "Confirm Refund"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

      </div>

      {billOrder && (
        <DeliveryBill order={billOrder} onClose={() => setBillOrder(null)} />
      )}

      {/* ── Image Preview Popup ── */}
      {previewImg && (
        <div className="img-preview-overlay" onClick={() => setPreviewImg(null)}>
          <div className="img-preview-modal" onClick={(e) => e.stopPropagation()}>
            <button className="img-preview-close" onClick={() => setPreviewImg(null)}>×</button>
            <img src={previewImg.src} alt={previewImg.name} className="img-preview-img" />
            <div className="img-preview-info">
              <div className="img-preview-id">{previewImg.id}</div>
              <div className="img-preview-name">{previewImg.name}</div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </>
  );
};

export default OrderDetails;
